/**
 * Building user input into PostgREST filter strings. Runtime-neutral: used by
 * the web data layer (`db-tenant.ts`) and by edge functions.
 */

/**
 * Escapes the LIKE metacharacters `\`, `%` and `_` so they match literally.
 *
 * Not `*`: PostgREST rewrites every `*` in a `like`/`ilike` operand to `%`
 * before Postgres sees it, and offers no escape for it, so a `*` the user types
 * stays a wildcard whatever is done here.
 */
export function escapeLikePattern(value: string): string {
  return value.replace(/[%_\\]/g, '\\$&')
}

/**
 * A quoted `"%value%"` operand for an `ilike` inside `.or(...)`: matches rows
 * that contain `value`, with `%`, `_` and `\` taken literally. `*` is the one
 * exception — PostgREST turns it into `%` and it cannot be escaped (see
 * {@link escapeLikePattern}), so it still matches any run of characters.
 *
 * Two escaping layers, in this order: LIKE (`%`, `_`, `\`) and then PostgREST's
 * quoted value, where a backslash escapes the next character. Skipping the
 * second layer lets PostgREST eat the LIKE escapes — `a\%b` reaches Postgres
 * as `a%b` and `%` is a wildcard again (verified against local PostgREST).
 * The double quotes keep `,` `.` `:` `(` `)` from being read as filter syntax.
 */
export function orFilterContainsPattern(value: string): string {
  const quoted = escapeLikePattern(value).replace(/["\\]/g, '\\$&')
  return `"%${quoted}%"`
}
